import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Save, ExternalLink, Building2, Clock, Tags, X, Plus, Mail, Bell, Phone, MessageSquare, Timer } from "lucide-react";
import { Provider } from "@/hooks/useProviders";
import { useOpeningHours } from "@/hooks/useOpeningHours";
import { useQueryClient } from "@tanstack/react-query";
import BrandingSettings from "./BrandingSettings";

interface ProviderSettingsProps {
  provider: Provider;
  onProviderUpdate: () => void;
}

interface SettingsFormData {
  displayName: string;
  stripeConnectAccountId: string;
  description: string;
  specialties: string[];
  providerContactEmail: string;
  emailNotificationsEnabled: boolean;
  providerNotifNumber: string;
  smsNotificationsEnabled: boolean;
  checkInWindowMinutes: number;
  earlyAccessEnabled: boolean;
  earlyAccessMinutes: number;
  minBookingAdvanceMinutes: number;
  checkoutReminderEnabled: boolean;
}

interface OpeningHoursFormData {
  [key: string]: {
    isOpen: boolean;
    openTime: string;
    closeTime: string;
  };
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

// Available specialties for selection
const AVAILABLE_SPECIALTIES = [
  'Yoga',
  'Pilates',
  'Fitness',
  'Massage',
  'Physiotherapy',
  'Personal Training',
  'Nutrition',
  'Meditation',
  'Spa',
  'Beauty',
  'Wellness',
  'Mental Health',
  'Counselling',
  'Chiropractic',
  'Osteopathy',
  'Acupuncture',
  'Reflexology',
  'Aromatherapy',
  'Reiki',
  'Life Coaching'
];

const ProviderSettings = ({ provider, onProviderUpdate }: ProviderSettingsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isOpeningHoursLoading, setIsOpeningHoursLoading] = useState(false);
  const [newSpecialty, setNewSpecialty] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: openingHours, isLoading: openingHoursDataLoading } = useOpeningHours(provider.id);

  const { register, handleSubmit, formState: { errors }, watch, setValue, getValues, control } = useForm<SettingsFormData>({
    defaultValues: {
      displayName: (provider as any).display_name || '',
      stripeConnectAccountId: provider.stripe_connect_account_id || '',
      description: provider.description || '',
      specialties: provider.specialties || [],
      providerContactEmail: (provider as any).provider_contact_email || '',
      emailNotificationsEnabled: (provider as any).email_notifications_enabled !== false,
      providerNotifNumber: (provider as any).provider_notif_number || '',
      smsNotificationsEnabled: (provider as any).sms_notifications_enabled || false,
      checkInWindowMinutes: (provider as any).check_in_window_minutes || 5,
      earlyAccessEnabled: (provider as any).early_access_enabled || false,
      earlyAccessMinutes: (provider as any).early_access_minutes || 1,
      minBookingAdvanceMinutes: (provider as any).min_booking_advance_minutes || 0,
      checkoutReminderEnabled: (provider as any).checkout_reminder_enabled || false,
    }
  });

  const specialties = watch("specialties");

  // Create default opening hours data
  const createDefaultOpeningHoursData = () => {
    return DAYS_OF_WEEK.reduce((acc, day) => {
      const existingHour = openingHours?.find(h => h.day_of_week === day.value);
      console.log(`Day ${day.value} (${day.label}):`, existingHour);
      
      acc[day.value] = {
        isOpen: existingHour ? !existingHour.is_closed : false,
        openTime: existingHour?.open_time || '09:00',
        closeTime: existingHour?.close_time || '17:00',
      };
      return acc;
    }, {} as OpeningHoursFormData);
  };

  const { control: openingHoursControl, handleSubmit: handleOpeningHoursSubmit, formState: { errors: openingHoursErrors }, reset: resetOpeningHours } = useForm<OpeningHoursFormData>({
    defaultValues: createDefaultOpeningHoursData()
  });

  // Reset opening hours form when data loads
  useEffect(() => {
    if (openingHours && !openingHoursDataLoading) {
      console.log('Opening hours data loaded:', openingHours);
      const newDefaultValues = createDefaultOpeningHoursData();
      console.log('New default values:', newDefaultValues);
      resetOpeningHours(newDefaultValues);
    }
  }, [openingHours, openingHoursDataLoading, resetOpeningHours]);

  const addSpecialty = (specialty: string) => {
    const currentSpecialties = getValues("specialties");
    if (specialty && !currentSpecialties.includes(specialty)) {
      setValue("specialties", [...currentSpecialties, specialty]);
      setNewSpecialty("");
    }
  };

  const removeSpecialty = (specialtyToRemove: string) => {
    const currentSpecialties = getValues("specialties");
    setValue("specialties", currentSpecialties.filter(s => s !== specialtyToRemove));
  };

  const onSubmit = async (data: SettingsFormData) => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('providers')
        .update({
          display_name: data.displayName || null,
          stripe_connect_account_id: data.stripeConnectAccountId || null,
          description: data.description || null,
          specialties: data.specialties,
          provider_contact_email: data.providerContactEmail || null,
          email_notifications_enabled: data.emailNotificationsEnabled,
          provider_notif_number: data.providerNotifNumber || null,
          sms_notifications_enabled: data.smsNotificationsEnabled,
          check_in_window_minutes: data.checkInWindowMinutes,
          early_access_enabled: data.earlyAccessEnabled,
          early_access_minutes: data.earlyAccessMinutes,
          min_booking_advance_minutes: data.minBookingAdvanceMinutes,
          checkout_reminder_enabled: data.checkoutReminderEnabled,
          updated_at: new Date().toISOString()
        })
        .eq('id', provider.id);

      if (error) throw error;

      toast({
        title: "Settings Updated",
        description: "Your provider settings have been successfully updated.",
      });
      
      onProviderUpdate();
    } catch (error) {
      console.error('Error updating provider settings:', error);
      toast({
        title: "Error",
        description: "Failed to update settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onOpeningHoursSubmit = async (data: OpeningHoursFormData) => {
    setIsOpeningHoursLoading(true);
    try {
      console.log('Submitting opening hours data:', data);
      
      // First, delete existing opening hours for this provider
      const { error: deleteError } = await supabase
        .from('opening_hours')
        .delete()
        .eq('provider_id', provider.id);

      if (deleteError) throw deleteError;

      // Insert new opening hours
      const openingHoursData = DAYS_OF_WEEK.map(day => ({
        provider_id: provider.id,
        day_of_week: day.value,
        is_closed: !data[day.value].isOpen,
        open_time: data[day.value].isOpen ? data[day.value].openTime : null,
        close_time: data[day.value].isOpen ? data[day.value].closeTime : null,
      }));

      console.log('Opening hours data to insert:', openingHoursData);

      const { error: insertError } = await supabase
        .from('opening_hours')
        .insert(openingHoursData);

      if (insertError) throw insertError;

      // Invalidate opening hours cache
      await queryClient.invalidateQueries({ queryKey: ['opening_hours', provider.id] });

      toast({
        title: "Opening Hours Updated",
        description: "Your opening hours have been successfully updated.",
      });
    } catch (error) {
      console.error('Error updating opening hours:', error);
      toast({
        title: "Error",
        description: "Failed to update opening hours. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsOpeningHoursLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Branding Settings */}
      <BrandingSettings 
        provider={provider as any} 
        onProviderUpdate={onProviderUpdate} 
      />

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg sm:text-xl">Business Information</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
            <div className="space-y-2">
              <Label htmlFor="displayName">
                Display Name
              </Label>
              <Input
                id="displayName"
                placeholder="e.g. NORDIC CYCLE"
                {...register("displayName", {
                  maxLength: {
                    value: 100,
                    message: "Display name must be less than 100 characters"
                  }
                })}
              />
              {errors.displayName && (
                <p className="text-sm text-red-600">{errors.displayName.message}</p>
              )}
              <p className="text-xs sm:text-sm text-gray-500">
                Custom name shown in headers. Leave empty to use "{provider.name}".
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                About Description
              </Label>
              <Textarea
                id="description"
                placeholder="Tell customers about your business, services, and what makes you unique..."
                rows={4}
                className="min-h-[100px] sm:min-h-[120px]"
                {...register("description", {
                  maxLength: {
                    value: 1000,
                    message: "Description must be less than 1000 characters"
                  }
                })}
              />
              {errors.description && (
                <p className="text-sm text-red-600">{errors.description.message}</p>
              )}
              <p className="text-xs sm:text-sm text-gray-500">
                This description will be displayed on your provider profile page.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="providerContactEmail" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Provider Contact Email
                </Label>
                <Input
                  id="providerContactEmail"
                  type="email"
                  placeholder="contact@yourprovider.com"
                  {...register("providerContactEmail", {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Please enter a valid email address"
                    }
                  })}
                />
                {errors.providerContactEmail && (
                  <p className="text-sm text-red-600">{errors.providerContactEmail.message}</p>
                )}
                <p className="text-xs sm:text-sm text-gray-500">
                  This email will be used for booking notifications and customer inquiries.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="providerNotifNumber" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Provider Phone Number (for SMS alerts only)
                </Label>
                <Input
                  id="providerNotifNumber"
                  type="tel"
                  placeholder="+44 7123 456789"
                  {...register("providerNotifNumber", {
                    pattern: {
                      value: /^\+44\s?\d{4}\s?\d{6}$|^\+44\d{10}$/,
                      message: "Please enter a valid UK phone number starting with +44"
                    }
                  })}
                />
                {errors.providerNotifNumber && (
                  <p className="text-sm text-red-600">{errors.providerNotifNumber.message}</p>
                )}
                <p className="text-xs sm:text-sm text-gray-500">
                  UK phone number starting with +44 for SMS notifications.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Notification Settings
              </Label>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium">Email Notifications</div>
                    <div className="text-xs text-gray-500">
                      Receive email notifications when new bookings are made
                    </div>
                  </div>
                  <Controller
                    name="emailNotificationsEnabled"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      SMS Notifications
                    </div>
                    <div className="text-xs text-gray-500">
                      Receive SMS alerts for new bookings and important updates
                    </div>
                  </div>
                  <Controller
                    name="smsNotificationsEnabled"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </div>
            </div>


            <div className="space-y-2">
              <Label htmlFor="checkInWindowMinutes" className="flex items-center gap-2">
                <Timer className="h-4 w-4" />
                Check-in Window (Minutes)
              </Label>
              <Input
                id="checkInWindowMinutes"
                type="number"
                min="1"
                max="120"
                placeholder="5"
                {...register("checkInWindowMinutes", {
                  required: "Check-in window is required",
                  min: {
                    value: 1,
                    message: "Check-in window must be at least 1 minute"
                  },
                  max: {
                    value: 120,
                    message: "Check-in window cannot exceed 120 minutes"
                  },
                  valueAsNumber: true
                })}
              />
              {errors.checkInWindowMinutes && (
                <p className="text-sm text-red-600">{errors.checkInWindowMinutes.message}</p>
              )}
              <p className="text-xs sm:text-sm text-gray-500">
                How many minutes before their session can customers check in? This affects when access codes become available.
              </p>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="space-y-0.5">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <Bell className="h-4 w-4" />
                  Checkout Reminder
                </Label>
                <div className="text-xs text-gray-500">
                  Send customers an email 5 minutes before their session ends, reminding them to check out. Late check-outs may incur a penalty.
                </div>
              </div>
              <Controller
                name="checkoutReminderEnabled"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Early Access Settings
              </Label>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="space-y-0.5">
                    <div className="text-sm font-medium">Allow Early Access</div>
                    <div className="text-xs text-gray-500">
                      When enabled, access codes will be issued with a slightly earlier start time to prevent access being denied just before the official session begins.
                    </div>
                  </div>
                  <Controller
                    name="earlyAccessEnabled"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </div>
                
                <Controller
                  name="earlyAccessEnabled"
                  control={control}
                  render={({ field: { value: isEarlyAccessEnabled } }) => (
                    <div className={`space-y-2 ${!isEarlyAccessEnabled ? 'opacity-50' : ''}`}>
                      <Label htmlFor="earlyAccessMinutes" className="text-sm">
                        Early Access Time
                      </Label>
                      <Controller
                        name="earlyAccessMinutes"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value.toString()}
                            onValueChange={(value) => field.onChange(parseInt(value))}
                            disabled={!isEarlyAccessEnabled}
                          >
                            <SelectTrigger className="w-full sm:w-48">
                              <SelectValue placeholder="Select minutes" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 minute</SelectItem>
                              <SelectItem value="2">2 minutes</SelectItem>
                              <SelectItem value="3">3 minutes</SelectItem>
                              <SelectItem value="4">4 minutes</SelectItem>
                              <SelectItem value="5">5 minutes</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <p className="text-xs text-gray-500">
                        Access codes will start this many minutes before the session start time
                      </p>
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="minBookingAdvanceMinutes" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Minimum Advance Booking Time (Minutes)
              </Label>
              <Input
                id="minBookingAdvanceMinutes"
                type="number"
                min="0"
                max="1440"
                placeholder="0"
                {...register("minBookingAdvanceMinutes", {
                  required: "This field is required",
                  min: {
                    value: 0,
                    message: "Must be 0 or greater"
                  },
                  max: {
                    value: 1440,
                    message: "Cannot exceed 1440 minutes (24 hours)"
                  },
                  valueAsNumber: true
                })}
              />
              {errors.minBookingAdvanceMinutes && (
                <p className="text-sm text-red-600">{errors.minBookingAdvanceMinutes.message}</p>
              )}
              <p className="text-xs sm:text-sm text-gray-500">
                Minimum minutes before a session that customers can book. Set to 0 for last-minute bookings, 60 for 1 hour notice, etc.
              </p>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <Label className="flex items-center gap-2">
                <Tags className="h-4 w-4" />
                Specialties
              </Label>
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
                {specialties.map((specialty) => (
                  <Badge key={specialty} variant="secondary" className="flex items-center gap-1 text-xs sm:text-sm">
                    <span className="truncate max-w-[120px] sm:max-w-none">{specialty}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-3 w-3 sm:h-4 sm:w-4 p-0 hover:bg-transparent"
                      onClick={() => removeSpecialty(specialty)}
                    >
                      <X className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
              
              <div className="space-y-2">
                <Select onValueChange={(value) => addSpecialty(value)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a specialty to add" />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_SPECIALTIES
                      .filter(specialty => !specialties.includes(specialty))
                      .map((specialty) => (
                        <SelectItem key={specialty} value={specialty}>
                          {specialty}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                
                <div className="flex gap-1 sm:gap-2">
                  <Input
                    placeholder="Custom specialty"
                    value={newSpecialty}
                    onChange={(e) => setNewSpecialty(e.target.value)}
                    className="flex-1 text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => addSpecialty(newSpecialty)}
                    disabled={!newSpecialty || specialties.includes(newSpecialty)}
                    className="shrink-0"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-500">
                Add specialties to help customers find your services. You can select from common options or add custom ones.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="stripeConnectAccountId">
                Stripe Connect Account ID
              </Label>
              <Input
                id="stripeConnectAccountId"
                placeholder="acct_1234567890"
                className="font-mono text-sm"
                {...register("stripeConnectAccountId", {
                  pattern: {
                    value: /^acct_[a-zA-Z0-9]+$/,
                    message: "Please enter a valid Stripe Connect account ID (starts with 'acct_')"
                  }
                })}
              />
              {errors.stripeConnectAccountId && (
                <p className="text-sm text-red-600">{errors.stripeConnectAccountId.message}</p>
              )}
              <p className="text-xs sm:text-sm text-gray-500">
                Your Stripe Connect account ID is required to receive payments from bookings.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.open('https://dashboard.stripe.com/connect/accounts', '_blank')}
                className="flex items-center gap-2 w-full sm:w-auto"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="hidden sm:inline">Manage Stripe Connect</span>
                <span className="sm:hidden">Manage Stripe</span>
              </Button>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2 w-full sm:w-auto"
              >
                <Save className="h-4 w-4" />
                {isLoading ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-lg sm:text-xl">Opening Hours</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {openingHoursDataLoading ? (
            <div className="text-center py-6 sm:py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">Loading opening hours...</p>
            </div>
          ) : (
            <form onSubmit={handleOpeningHoursSubmit(onOpeningHoursSubmit)} className="space-y-3 sm:space-y-4">
              {DAYS_OF_WEEK.map((day) => (
                <div key={day.value} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 border rounded-lg">
                  <div className="font-medium text-sm sm:text-base sm:w-20 flex-shrink-0">
                    {day.label}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 flex-1">
                    <Controller
                      name={`${day.value}.isOpen` as any}
                      control={openingHoursControl}
                      render={({ field }) => (
                        <Select
                          value={field.value ? "open" : "closed"}
                          onValueChange={(value) => field.onChange(value === "open")}
                        >
                          <SelectTrigger className="w-full sm:w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />

                    <Controller
                      name={`${day.value}.isOpen` as any}
                      control={openingHoursControl}
                      render={({ field: { value: isOpen } }) => (
                        <div className={`flex items-center gap-2 sm:gap-4 flex-1 ${!isOpen ? 'opacity-50' : ''}`}>
                          <Controller
                            name={`${day.value}.openTime` as any}
                            control={openingHoursControl}
                            rules={{
                              required: isOpen ? "Open time is required" : false
                            }}
                            render={({ field }) => (
                              <Input
                                type="time"
                                disabled={!isOpen}
                                className="w-full sm:w-32 text-sm"
                                value={typeof field.value === 'string' ? field.value : ''}
                                onChange={field.onChange}
                              />
                            )}
                          />
                          
                          <span className="text-gray-500 text-sm hidden sm:inline">to</span>
                          <span className="text-gray-500 text-sm sm:hidden">–</span>
                          
                          <Controller
                            name={`${day.value}.closeTime` as any}
                            control={openingHoursControl}
                            rules={{
                              required: isOpen ? "Close time is required" : false
                            }}
                            render={({ field }) => (
                              <Input
                                type="time"
                                disabled={!isOpen}
                                className="w-full sm:w-32 text-sm"
                                value={typeof field.value === 'string' ? field.value : ''}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </div>
                      )}
                    />
                  </div>
                </div>
              ))}

              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={isOpeningHoursLoading}
                  className="flex items-center gap-2 w-full sm:w-auto"
                >
                  <Save className="h-4 w-4" />
                  {isOpeningHoursLoading ? "Saving..." : "Save Opening Hours"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProviderSettings;
